/**
 * ログイン中のストア（サーバ `/me/*`）。
 *
 * 認証は Cookie（M6a の会員セッション）に載るため、ここでは資格情報を扱わず
 * `credentials: 'same-origin'` を付けるだけ。失敗は例外にせず握って縮退する
 * （しおりが保存できないことより、読書が止まらないことを優先する）。
 */
import type { ListType, MarkType, ProgressEntry, UserDataStore, UserItem } from './store';
import { nowSec } from './store';

interface ItemsResponse {
  items?: UserItem[];
}

export class RemoteUserData implements UserDataStore {
  /** @param base API の基準（例 `/api/v1`）。 */
  constructor(private readonly base: string) {}

  async list(type: ListType): Promise<UserItem[]> {
    const data = await this.request<ItemsResponse>(`/me/data/${type}`, 'GET');
    return data?.items ?? [];
  }

  async put(type: MarkType, item: UserItem): Promise<void> {
    await this.request(`/me/data/${type}`, 'PUT', {
      book: item.book,
      page: item.page,
      payload: item.payload,
      updatedAt: item.updatedAt,
    });
  }

  async remove(type: MarkType, book: string, page?: number): Promise<void> {
    await this.request(`/me/data/${type}`, 'DELETE', { book, page });
  }

  async progress(book: string): Promise<number | null> {
    const data = await this.request<{ page?: number | null }>(
      `/me/progress/${encodeURIComponent(book)}`,
      'GET',
    );
    return data?.page ?? null;
  }

  async saveProgress(book: string, page: number, updatedAt: number = nowSec()): Promise<void> {
    await this.request(`/me/progress/${encodeURIComponent(book)}`, 'PUT', { page, updatedAt });
  }

  /** ログイン時のマージ送信（集合系はまとめて 1 往復）。 */
  async putBulk(type: MarkType, items: readonly UserItem[]): Promise<void> {
    if (items.length === 0) return;
    await this.request(`/me/data/${type}`, 'PUT', { items });
  }

  /** 位置系のマージ送信（サーバ側が新しい方を残す）。 */
  async putProgress(entries: readonly ProgressEntry[]): Promise<void> {
    for (const entry of entries) {
      await this.saveProgress(entry.book, entry.page, entry.updatedAt);
    }
  }

  private async request<T>(path: string, method: string, body?: unknown): Promise<T | null> {
    try {
      const response = await fetch(this.base + path, {
        method,
        credentials: 'same-origin',
        ...(body === undefined
          ? {}
          : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
      });
      if (!response.ok) return null;
      return (await response.json()) as T;
    } catch {
      return null; // ネットワーク断でも読書を止めない
    }
  }
}
